import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';
import { concat, isEmpty, isNil } from 'lodash';

import { uniqueNamesGenerator, adjectives, animals, colors } from 'unique-names-generator';
import * as mapOps from '../../../api/Maps/map-operations';
import Maps from '../../../api/Maps/Maps';
import Layers from '../../../api/Layers/Layers';
import useTimeStore from '../../components/ZustandProvider';
import Loading from '../../components/Loading';
import CesiumViewerContainerViewMap from '../../containers/CesiumViewerContainerViewMap';
import LoadingComponent from '../../components/LoadingComponent';
import MapContainerViewMap from '../../containers/MapContainerViewMap';
import NotFound from '../NotFound';
import NewLayout from '../../containers/MapContainerMA';
import UserSharedModal from '../../components/UserSharedModal';

const { isBeta, isAdvanced } = Meteor.settings.public;

const createUserLocalId = () => {
  let userIpsilum = JSON.parse(localStorage.getItem('userIpsilum'));

  if (!userIpsilum || !userIpsilum.id) {
    const newId = Array.from({ length: 18 }, () => Math.random().toString(36)[2]).join('');

    // Generar un nombre único usando la librería
    const generatedName = uniqueNamesGenerator({
      dictionaries: [adjectives, colors, animals],
      separator: ' ',
      style: 'capital'
    });

    userIpsilum = {
      id: newId,
      name: generatedName
    };
    localStorage.setItem('userIpsilum', JSON.stringify(userIpsilum));
  }

  return userIpsilum;
};

const PublicMap = ({
  loading,
  map,
  layers,
  match,
  deployEnv,
  history,
  isFullScreen,
  isAtlas,
  userControlSetting,
  openModalUserPrivate,
  sharedUser
}) => {
  if (loading) return <Loading />;
  if (isEmpty(map)) return <NotFound />;

  const MapProps = {
    baseMaps: mapOps.getLayerObjectsArray(map, layers, false),
    deployEnv,
    history,
    isAtlas,
    externalBoton: true,
    layers: mapOps.getLayerObjectsArray(map, layers),
    match,
    map,
    userControlSetting
  };
  const [openModal, setOpenModal] = React.useState(
    openModalUserPrivate || mapOps.getIsNameShared(map) === true
  );
  const [nameToEnter, setNameToEnter] = React.useState('');
  const [userToSecondEnter, setUserToSecondEnter] = React.useState({
    id: '',
    name: '',
    password: ''
  });
  const userIpsilum = createUserLocalId();

  // Determinar el nombre final (prioridad: nameToEnter > localStorage)
  const userName = nameToEnter || userIpsilum.name;

  // Actualizar localStorage si el nombre cambia
  React.useEffect(() => {
    if (nameToEnter && nameToEnter !== userIpsilum.name) {
      const updatedUser = { ...userIpsilum, name: nameToEnter };
      localStorage.setItem('userIpsilum', JSON.stringify(updatedUser));
    }
  }, [nameToEnter]);

  const userProps = {
    userId: userToSecondEnter.id || userIpsilum.id,
    roles: 'premium',
    name: userName,
    emailAddress: '',
    userLocalId: userIpsilum
  };

  return (
    <>
      <UserSharedModal
        map={{ ...map }}
        nameToEnter={nameToEnter}
        setNameToEnter={setNameToEnter}
        openModal={openModal}
        setOpenModal={setOpenModal}
        userProps={userProps}
        userToSecondEnter={userToSecondEnter}
        setUserToSecondEnter={setUserToSecondEnter}
        sharedUser={sharedUser}
      />
      <Row className={isAtlas ? 'h-100 mx-0' : ''}>
        <Col xs={12} className={isAtlas ? 'h-100 px-0' : ' px-0'}>
          {(mapOps.isLeafletMap(map) || mapOps.isIASMap(map)) &&
            (mapOps.areFeature(map) && isAdvanced ? (
              <>
                {!isBeta && <LoadingComponent adjustMapProp />}
                <NewLayout
                  MapProps={MapProps}
                  isPublicMap
                  userControlSetting={userControlSetting}
                  userProps={userProps}
                />
              </>
            ) : (
              <MapContainerViewMap
                baseMaps={mapOps.getLayerObjectsArray(map, layers, false)}
                deployEnv={deployEnv}
                isAtlas={isAtlas}
                isFullScreen={isFullScreen}
                layers={mapOps.getLayerObjectsArray(map, layers)}
                map={map}
                userControlSetting={userControlSetting}
              />
            ))}
          {mapOps.isCesiumMap(map) && (
            <CesiumViewerContainerViewMap
              baseMaps={mapOps.getLayerObjectsArray(map, layers, false)}
              isAtlas={isAtlas}
              isFullScreen={isFullScreen}
              layers={mapOps.getLayerObjectsArray(map, layers)}
              map={map}
              userControlSetting={userControlSetting}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

PublicMap.defaultProps = {
  loading: true,
  map: {},
  layers: [],
  isAtlas: false,
  history: {},
  match: {},
  userControlSetting: {},
  openModalUserPrivate: false,
  sharedUser: false
};

PublicMap.propTypes = {
  loading: PropTypes.bool,
  map: PropTypes.object,
  layers: PropTypes.array,
  isAtlas: PropTypes.bool,
  deployEnv: PropTypes.string.isRequired,
  isFullScreen: PropTypes.bool.isRequired,
  history: PropTypes.object,
  match: PropTypes.object,
  userControlSetting: PropTypes.object,
  openModalUserPrivate: PropTypes.bool,
  sharedUser: PropTypes.bool
};

export default withTracker(
  ({ match, privateShared, sharedPrivateUser, sharedUser, shareAnnotation }) => {
    if (privateShared) {
      const mapPublicId = match.params.public_map_id;
      const { variable } = match.params;
      const mapSub = Meteor.isClient && Meteor.subscribe('maps.publicPrivate', mapPublicId);
      const map = Maps.findOne({ publicPrivateId: mapPublicId });
      if (!isEmpty(variable)) {
        const parameters = JSON.parse(
          `{"${variable.replace(/&/g, '","').replace(/=/g, '":"')}"}`,
          (key, value) => {
            return key === '' ? value : decodeURIComponent(value);
          }
        );
        const longlat = [Number(parameters.lng), Number(parameters.lat)];
        // eslint-disable-next-line no-restricted-globals
        if (!isEmpty(map) && !longlat.some(isNaN)) {
          map.mapLocation.geometry.coordinates = longlat;
          const zoom = isEmpty(parameters.zoom) ? 15 : Number(parameters.zoom);
          map.mapLocation.properties.zoom = zoom;
        }
      }
      let layerIds = [];
      if (!isNil(map)) layerIds = concat(mapOps.getLayers(map), mapOps.getBaseMaps(map));

      const layersSub = Meteor.isClient && Meteor.subscribe('layers.viewMap', layerIds);

      return {
        loading: !Meteor.isClient || !mapSub.ready() || !layersSub.ready(),
        map,
        layers: Layers.find({ _id: { $in: layerIds }, deleted: { $eq: 'no' } }).fetch()
      };
    }

    if (sharedPrivateUser) {
      const mapPublicId = match.params.public_map_id;
      const { variable } = match.params;
      const mapSub = Meteor.isClient && Meteor.subscribe('maps.publicUserPrivate', mapPublicId);
      const map = Maps.findOne({ publicUserPrivateId: mapPublicId });
      if (!isEmpty(variable)) {
        const parameters = JSON.parse(
          `{"${variable.replace(/&/g, '","').replace(/=/g, '":"')}"}`,
          (key, value) => {
            return key === '' ? value : decodeURIComponent(value);
          }
        );
        const longlat = [Number(parameters.lng), Number(parameters.lat)];
        // eslint-disable-next-line no-restricted-globals
        if (!isEmpty(map) && !longlat.some(isNaN)) {
          map.mapLocation.geometry.coordinates = longlat;
          const zoom = isEmpty(parameters.zoom) ? 15 : Number(parameters.zoom);
          map.mapLocation.properties.zoom = zoom;
        }
      }
      let layerIds = [];
      if (!isNil(map)) layerIds = concat(mapOps.getLayers(map), mapOps.getBaseMaps(map));

      const layersSub = Meteor.isClient && Meteor.subscribe('layers.viewMap', layerIds);

      return {
        loading: !Meteor.isClient || !mapSub.ready() || !layersSub.ready(),
        map,
        layers: Layers.find({ _id: { $in: layerIds }, deleted: { $eq: 'no' } }).fetch()
      };
    }

    if (sharedUser) {
      const mapPublicId = match.params.public_map_id;
      const { variable } = match.params;
      const mapSub = Meteor.isClient && Meteor.subscribe('maps.publicUser', mapPublicId);
      const map = Maps.findOne({ publicUserId: mapPublicId });
      if (!isEmpty(variable)) {
        const parameters = JSON.parse(
          `{"${variable.replace(/&/g, '","').replace(/=/g, '":"')}"}`,
          (key, value) => {
            return key === '' ? value : decodeURIComponent(value);
          }
        );
        const longlat = [Number(parameters.lng), Number(parameters.lat)];
        // eslint-disable-next-line no-restricted-globals
        if (!isEmpty(map) && !longlat.some(isNaN)) {
          map.mapLocation.geometry.coordinates = longlat;
          const zoom = isEmpty(parameters.zoom) ? 15 : Number(parameters.zoom);
          map.mapLocation.properties.zoom = zoom;
        }
      }
      let layerIds = [];
      if (!isNil(map)) {
        layerIds = concat(mapOps.getLayers(map), mapOps.getBaseMaps(map));
      }
      const layersSub = Meteor.isClient && Meteor.subscribe('layers.viewPublicMap', layerIds);
      const sizeDrawerSelected =
        map && map.isFeature === true && !isEmpty(map.settingsAdvancedMap)
          ? map.settingsAdvancedMap.sizeDrawer.value
          : 0;
      useTimeStore.setState({ drawerWidth: sizeDrawerSelected });
      return {
        loading: !Meteor.isClient || !mapSub.ready() || !layersSub.ready(),
        map,
        layers: Layers.find({ _id: { $in: layerIds }, deleted: { $eq: 'no' } }).fetch()
      };
    }

    if (shareAnnotation) {
      const mapPublicId = match.params.public_map_id;
      const { variable } = match.params;

      // Si tienes lógica adicional relacionada a annotationId, podrías agregarla aquí
      const mapSub = Meteor.isClient && Meteor.subscribe('maps.public', mapPublicId);
      const map = Maps.findOne({ publicId: mapPublicId });

      if (!isEmpty(variable)) {
        const parameters = JSON.parse(
          `{"${variable.replace(/&/g, '","').replace(/=/g, '":"')}"}`,
          (key, value) => {
            return key === '' ? value : decodeURIComponent(value);
          }
        );
        const longlat = [Number(parameters.lng), Number(parameters.lat)];
        // eslint-disable-next-line no-restricted-globals
        if (!isEmpty(map) && !longlat.some(isNaN)) {
          map.mapLocation.geometry.coordinates = longlat;
          const zoom = isEmpty(parameters.zoom) ? 15 : Number(parameters.zoom);
          map.mapLocation.properties.zoom = zoom;
        }
      }

      let layerIds = [];
      if (!isNil(map)) {
        layerIds = concat(mapOps.getLayers(map), mapOps.getBaseMaps(map));
      }

      const layersSub = Meteor.isClient && Meteor.subscribe('layers.viewPublicMap', layerIds);
      const sizeDrawerSelected =
        map && map.isFeature === true && !isEmpty(map.settingsAdvancedMap)
          ? map.settingsAdvancedMap.sizeDrawer.value
          : 0;

      useTimeStore.setState({ drawerWidth: sizeDrawerSelected });

      return {
        loading: !Meteor.isClient || !mapSub.ready() || !layersSub.ready(),
        map,
        layers: Layers.find({ _id: { $in: layerIds }, deleted: { $eq: 'no' } }).fetch()
      };
    }

    const mapPublicId = match.params.public_map_id;
    const { variable } = match.params;
    const mapSub = Meteor.isClient && Meteor.subscribe('maps.public', mapPublicId);
    const map = Maps.findOne({ publicId: mapPublicId });
    if (!isEmpty(variable)) {
      const parameters = JSON.parse(
        `{"${variable.replace(/&/g, '","').replace(/=/g, '":"')}"}`,
        (key, value) => {
          return key === '' ? value : decodeURIComponent(value);
        }
      );
      const longlat = [Number(parameters.lng), Number(parameters.lat)];
      // eslint-disable-next-line no-restricted-globals
      if (!isEmpty(map) && !longlat.some(isNaN)) {
        map.mapLocation.geometry.coordinates = longlat;
        const zoom = isEmpty(parameters.zoom) ? 15 : Number(parameters.zoom);
        map.mapLocation.properties.zoom = zoom;
      }
    }
    let layerIds = [];
    if (!isNil(map)) {
      layerIds = concat(mapOps.getLayers(map), mapOps.getBaseMaps(map));
    }
    const layersSub = Meteor.isClient && Meteor.subscribe('layers.viewPublicMap', layerIds);
    const sizeDrawerSelected =
      map && map.isFeature === true && !isEmpty(map.settingsAdvancedMap)
        ? map.settingsAdvancedMap.sizeDrawer.value
        : 0;
    useTimeStore.setState({ drawerWidth: sizeDrawerSelected });
    return {
      loading: !Meteor.isClient || !mapSub.ready() || !layersSub.ready(),
      map,
      layers: Layers.find({ _id: { $in: layerIds }, deleted: { $eq: 'no' } }).fetch()
    };
  }
)(PublicMap);
